import { MouseEvent } from 'react';
import { X, FileText, Globe, File, Image, Code } from 'lucide-react';
import { cn } from '@/lib/utils.js';
import { Artifact, ArtifactType } from '@/stores/artifactPanelStore.js';
import { Button } from '@/components/ui/button.js';

interface TabProps {
  artifact: Artifact;
  isActive: boolean;
  onClick: () => void;
  onClose: () => void;
  onCloseOthers: () => void;
}

const iconMap: Record<ArtifactType, React.ComponentType<{ className?: string }>> = {
  note: FileText,
  'notes-list': FileText,
  file: File,
  image: Image,
  code: Code,
  browser: Globe,
};

export function Tab({ artifact, isActive, onClick, onClose }: TabProps) {
  const Icon = iconMap[artifact.type];

  const handleClose = (e: MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <div
      className={cn(
        'group relative flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors select-none max-w-[200px]',
        isActive
          ? 'bg-muted text-foreground'
          : 'hover:bg-muted/50 text-muted-foreground'
      )}
      onClick={onClick}
    >
      {/* Icon */}
      <Icon className="h-4 w-4 flex-shrink-0" />

      {/* Title */}
      <span className="flex-1 text-sm truncate">
        {artifact.title}
      </span>

      {/* Unsaved indicator */}
      {artifact.metadata?.unsaved && (
        <div className="h-1.5 w-1.5 rounded-full bg-orange-500 flex-shrink-0" />
      )}

      {/* Close button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-5 w-5 flex-shrink-0 opacity-0 group-hover:opacity-100 hover:bg-background transition-opacity"
        onClick={handleClose}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
